import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import type { ProjectStats } from '../types/api';

interface ProjectUptimeChartProps {
  stats: ProjectStats[] | null;
  onProjectClick: (projectName: string) => void;
  selectedProject: string;
}

export function ProjectUptimeChart({ stats, onProjectClick, selectedProject }: ProjectUptimeChartProps) {
  if (!stats || stats.length === 0) {
    return (
      <div className="bg-dark-100 rounded-lg p-6 shadow-xl border border-primary-900/30">
        <h2 className="text-2xl font-bold text-primary-400 mb-4 flex items-center gap-2">
          <span className="text-3xl">📊</span>
          Uptime по проектам
        </h2>
        <div className="text-center py-12 text-dark-500">
          <span className="text-5xl mb-4 block">📭</span>
          <p className="text-lg">Нет данных для отображения</p>
        </div>
      </div>
    );
  }

  // Подготавливаем данные для графика
  const chartData = stats.map((stat) => ({
    name: stat.extensionName,
    uptime: parseFloat(stat.uptimePercent.toFixed(1)),
    total: stat.total,
    errorCount: stat.errorCount,
    isLowUptime: stat.uptimePercent < 99,
  }));

  // Кастомный tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-dark-100 border border-primary-500 rounded-lg p-4 shadow-xl">
          <p className="font-bold text-primary-400 mb-2">{data.name}</p>
          <div className="space-y-1 text-sm">
            <p className="text-dark-700">
              <span className="font-semibold">Uptime:</span>{' '}
              <span className={data.uptime >= 99 ? 'text-green-400' : 'text-red-400'}>
                {data.uptime}%
              </span>
            </p>
            <p className="text-dark-700">
              <span className="font-semibold">Всего событий:</span> {data.total.toLocaleString()}
            </p>
            <p className="text-dark-700">
              <span className="font-semibold">Ошибок:</span> <span className="text-red-400">{data.errorCount}</span>
            </p>
          </div>
          <p className="text-xs text-dark-500 mt-2 italic">Нажмите для подробностей</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-dark-100 rounded-lg p-6 shadow-xl border border-primary-900/30">
      <h2 className="text-2xl font-bold text-primary-400 mb-4 flex items-center gap-2">
        <span className="text-3xl">📊</span>
        Uptime по проектам
        <span className="text-lg text-dark-500 font-normal ml-2">
          ({stats.length} проектов)
        </span>
      </h2>

      <div className="mb-4 p-3 bg-dark-50 rounded-lg border border-dark-200">
        <p className="text-sm text-dark-600">
          <span className="font-semibold text-primary-400">💡 Подсказка:</span>{' '}
          Нажмите на столбец, чтобы выбрать проект и увидеть детальную информацию.
          Красные столбцы указывают на uptime ниже 99%.
        </p>
      </div>

      <div className="h-[400px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              height={100}
              tick={{ fill: '#9CA3AF', fontSize: 12 }}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fill: '#9CA3AF' }}
              label={{ value: 'Uptime (%)', angle: -90, position: 'insideLeft', fill: '#9CA3AF' }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
            <Bar
              dataKey="uptime"
              radius={[8, 8, 0, 0]}
              onClick={(data) => {
                if (data && data.name) {
                  onProjectClick(data.name);
                }
              }}
              cursor="pointer"
            >
              {chartData.map((entry, index) => {
                const isSelected = entry.name === selectedProject;
                const isLowUptime = entry.uptime < 99;
                
                let fillColor = '#3B82F6'; // primary-500 - синий по умолчанию
                
                if (isSelected) {
                  fillColor = '#8B5CF6'; // violet-500 - фиолетовый для выбранного
                } else if (isLowUptime) {
                  fillColor = '#EF4444'; // red-500 - красный для низкого uptime
                }
                
                return <Cell key={`cell-${index}`} fill={fillColor} />;
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Легенда */}
      <div className="mt-4 flex items-center justify-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-500 rounded"></div>
          <span className="text-dark-600">Нормальный uptime (≥99%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-500 rounded"></div>
          <span className="text-dark-600">Низкий uptime (&lt;99%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-violet-500 rounded"></div>
          <span className="text-dark-600">Выбранный проект</span>
        </div>
      </div>
    </div>
  );
}

